import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { ReleasesComponent } from '../../releases/releases.component';
import { AddDescriptionDialogComponent } from '../add-description/add-description-dialog.component';
import { ReleaseReportService } from 'src/app/services/releasereport.service';
import { ReleaseDetail } from 'src/app/models/release-detail';
import { ReviewClassification, AdoReleaseEnvironment } from 'src/app/models/release';
import { ReleaseEnvironmentService } from 'src/app/services/release-environment.service';
import { LoadingService } from 'src/app/services/loading.service';


@Component({
  selector: 'app-edit-release-dialog',
  templateUrl: './edit-release-dialog.component.html',
  styleUrls: [
    './edit-release-dialog.component.css',
    '../../home/home.component.css',
  ],
  standalone: false,
})
export class EditReleaseDialogComponent implements OnInit {
  currentIndex: number = 0;
  descriptions: ReleaseDetail[] = [];
  currentDescription: ReleaseDetail;
  ReviewClassification = ReviewClassification; // Make enum available to template
  failingEnvironments: AdoReleaseEnvironment[] = [];
  reviewClassifications = Object.values(ReviewClassification).filter(
    (value) => typeof value === 'number'
  ) as ReviewClassification[];

  get reviewClassification(): ReviewClassification {
    return this.data.reviewClassification;
  }

  set reviewClassification(value: ReviewClassification) {
    this.data.reviewClassification = value;
    this.data.isTransient = this.isTransientClassification(value);
  }

  constructor(
    public dialogRef: MatDialogRef<ReleasesComponent>,
    private releaseReportService: ReleaseReportService,
    private readonly releaseEnvironmentService: ReleaseEnvironmentService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialog: MatDialog,
    public readonly loadingService: LoadingService
  ) {
    this.descriptions = data.description;
    this.setCurrentDescription();
    this.currentDescription = data.description[0];

    // Set initial review classification based on reviewed state only
    if (!data.reviewClassification) {
      this.reviewClassification = data.reviewed === true
        ? ReviewClassification.Reviewed
        : ReviewClassification.NeedsReview;
    }
    
    // Ensure changeFail is initialized
    if (data.changeFail === undefined || data.changeFail === null) {
      data.changeFail = false;
    }
    
    // Ensure releaseEnvironments is properly initialized
    if (!data.releaseEnvironments) {
      data.releaseEnvironments = [];
    }
    
    // Get failing environments
    this.failingEnvironments = this.releaseEnvironmentService.getFailingEnvironments(data.releaseEnvironments);

    // Set width constraint on the dialog to prevent horizontal scrolling
    this.dialogRef.updateSize('600px', '');
  }
  
  ngOnInit(): void {
    // Load the release snapshot from ADO to get the environment IDs
    this.loadReleaseSnapshot();
  }
  
  /**
   * Load the release snapshot from ADO to get the environment IDs
   */
  loadReleaseSnapshot(): void {
    if (!this.data.adoReleaseId) {
      console.warn('Missing required data to fetch release snapshot');
      return;
    }
    
    this.loadingService.show('releaseSnapshot');
    
    this.releaseEnvironmentService.loadReleaseSnapshot(this.data.adoReleaseId)
      .subscribe({
        next: (releaseSnapshot) => {
          if (releaseSnapshot?.environments) {
            this.releaseEnvironmentService.mapEnvironmentsFromSnapshot(
              releaseSnapshot.environments,
              this.data.releaseEnvironments
            );
          } else {
            console.warn('No environments found in release snapshot');
          }
        },
        error: (error) => {
          console.error('Error loading release snapshot:', error);
          this.loadingService.hide('releaseSnapshot');
        }
      });
  }

  /**
   * Get CSS class for status styling
   * @param status The environment status
   * @returns CSS class based on status
   */
  getStatusClass(status: string): string {
    status = status?.toLowerCase() || '';
    
    if (status.includes('succeeded') || status.includes('success')) {
      return 'status-success';
    } else if (status.includes('failed') || status.includes('failure')) {
      return 'status-failure';
    } else if (status.includes('inprogress') || status.includes('in progress') || status.includes('pending')) {
      return 'status-inprogress';
    } else if (status.includes('canceled') || status.includes('cancelled')) {
      return 'status-canceled';
    } else {
      return 'status-unknown';
    }
  }
  
  /**
   * Get the environment URL with fallback
   */
  getEnvironmentUrl(environmentName: string): string {
    // For YAML pipelines, simply use the release link
    if (this.data.isYaml) {
      return this.data.releaseLink;
    }
    
    // For classic pipelines, use the environment-specific URL
    return this.releaseEnvironmentService.getEnvironmentUrlFallback(
      environmentName,
      this.data.adoReleaseId,
      this.data.releaseEnvironments
    );
  }
  
  /**
   * Format a timestamp for display
   * @param timeString The timestamp string
   * @returns Formatted date/time string
   */
  formatTime(timeString: string): string {
    if (!timeString) return '';
    
    // Handle default/empty time values in different formats
    if (timeString === '0001-01-01T00:00:00' || 
        timeString === '0001-01-01T00:00:00Z' ||
        timeString === '0001-01-01T00:00:00.0000000' ||
        timeString === '0001-01-01T00:00:00.0000000Z') {
      return '';
    }
    
    try {
      const date = new Date(timeString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', timeString);
        return '';
      }
      
      return date.toLocaleString('en-US', {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  isTransientClassification(classification: ReviewClassification): boolean {
    return (
      classification !== ReviewClassification.NeedsReview &&
      classification !== ReviewClassification.Reviewed
    );
  }

  get formattedDescription(): string {
    return this.data.description
      .map((desc: ReleaseDetail) => desc.Description)
      .join('\n');
  }

  setCurrentDescription() {
    this.currentDescription = this.descriptions[this.currentIndex];
  }

  nextDescription() {
    if (this.currentIndex < this.descriptions.length - 1) {
      this.currentIndex++;
      this.setCurrentDescription();
    }
  }

  previousDescription() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.setCurrentDescription();
    }
  }

  addDescription(value: string) {
    this.descriptions[this.currentIndex].Description = value;
  }

  openAddDescriptionModal(
    release: any,
    descriptionToUpdate?: ReleaseDetail
  ): void {
    const dialogRef = this.dialog.open(AddDescriptionDialogComponent, {
      width: '600px',
      panelClass: 'release-edit-dialog',
      data: {
        ...release,
        descriptionToUpdate,
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.data = result;
        this.setCurrentDescription();

        // Handle new description
        if (result.newDescription) {
          const body = {
            ReleaseId: this.data.id,
            Description: result.newDescription.Description,
            DescriptionId: result.newDescription.DescriptionId,
          };
          this.releaseReportService.addDetails(body).subscribe();
        }

        // Handle description update
        if (result.updatedDescription) {
          this.releaseReportService
            .updateDescription(result.updatedDescription)
            .subscribe();
        }

        if (result.closeOutEditReleaseModal) {
          this.dialogRef.close(this.data);
        }
      }
    });
  }

  onClose(): void {
    // Cancel: close with NO payload. `data` is two-way-bound to the form (e.g. the review
    // classification dropdown), so returning it here is indistinguishable from a Save — callers
    // would write-through-cache and locally apply a change the user explicitly cancelled (and that
    // was never PUT to the server). Returning undefined makes every `afterClosed` guard skip.
    this.dialogRef.close();
  }

  updateRelease(): void {    
    let body = {
      ReleaseId: this.data.id,
      ReviewClassification: this.data.reviewClassification,
      Reviewed: this.data.reviewed,
      IsTransient: this.data.isTransient,
      ChangeFail: this.data.changeFail,
    };
    
    console.log('Updating release with:', body);
    
    this.releaseReportService.updateRelease(body).subscribe({
      next: (response) => {
        this.dialogRef.close(this.data);
      },
      error: (error) => {
        console.error('Error updating release:', error);
      }
    });
  }
}
